/**
 * Single source of truth for "does this order match the search box?".
 *
 * Each order list page (MyOrders / DepartmentOrders / Approvals) used to roll
 * its own filter, and the field lists drifted — DeptOrders forgot `po_number`,
 * MyOrders forgot `personal_ref`, etc. Importing this keeps them in lockstep.
 *
 * TWO tiers, and they match DIFFERENTLY — the rule lives in
 * `utils/textSearchMatch.js`: prose fields (name, supplier, notes) match as a
 * case-insensitive SUBSTRING; the identifiers below match EXACTLY over a fold.
 *
 *   if (orderMatchesSearch(order, query)) {  ... include in filtered list  }
 *
 * Empty / whitespace-only queries match every order.
 */

import { formatOrderNumber } from './orderTitle';
import { normalisePoNumber } from './poNumber';
import { foldIdentifier, matchesTextSearch } from './textSearchMatch';

/**
 * 🔴 **`reference_number` IS SEARCHABLE AND IS DISPLAYED NOWHERE — 2026-08-12.**
 * Owner: *"we only show the true order ID never a concatinated order ID and
 * production like `AURORA-ORD-120` anywhere JUST ORDER ID AND PO"*. Its entry
 * here is the ONE surviving read of the column, deliberately: paperwork already
 * sent to a supplier carries the old string, so being findable is not being
 * shown. Do not remove it, and do not read its presence as licence to render it.
 *
 * ❌ **`personal_ref` was REMOVED from this list on 2026-08-12** — owner: *"remove
 * personal ref and use order ID for all look up's, pdfs, tiles etc where personal
 * ref was being used."* The per-department order number is now the lookup key it
 * used to be, and it is displayed everywhere personal_ref never was.
 */
const SEARCHABLE_FIELDS = [
  'reference_number',
  'po_number',
  'requested_by',
  // The Job Name column's row key ("Job / Rig / Gag" field) — owner finding
  // 2026-09-13: Pete searched a job name on Dept Orders and got no result
  // because this field was missing here. See OrderTableView.js's `job_name`
  // column (`sortField: 'job_rig'`).
  'job_rig',
  'supplier_name',
  'company_name',
  'production_name',
  'department',
  'status',
  'urgency',
  'notes',
  // Per-row line item text. `items_search_text` (every line's description +
  // part number) is SENT by `GET /api/orders` (My Orders) and REBUILT
  // client-side by Department Orders from `preview_items` (`withItemsSearchText`
  // below) — `GET /api/orders/department` stopped sending it 2026-09-26.
  // `first_item_description` is sent by those two endpoints as well.
  // `GET /api/approvals/pending` sends NEITHER, so To Approve's search never
  // matches item text (unchanged; an owner option). A missing field never matches.
  'items_search_text',
  'first_item_description',
  'first_item_cost_code',
];

/**
 * ── THE IDENTIFIER TIER — EXACT, WHERE THE TEXT TIER IS SUBSTRING ────────────
 *
 * 🚨 **`order_number` IS THE NUMBER SHOWN MOST PROMINENTLY IN THE APP, so it must
 * be the findable one.** A user reads `ORD-004` off the screen and quotes it to
 * a buyer; typing `ORD-004`, `004` or `4` must find that department's 4th order.
 *
 * 🔑 **WHY THIS IS NOT `SEARCHABLE_FIELDS.push('order_number')`.** The loop below
 * is `String(fieldValue).includes(query)` — the FIELD must contain the QUERY —
 * and that direction is wrong in BOTH directions at once for a bare integer:
 *   - the stored value `4` does not contain the query `ORD-004`, so the DISPLAYED
 *     form would find nothing;
 *   - the query `4` would match `order_number` 14 and 40, so the BARE form would
 *     return noise. ⚠️ **The over-match is the specific hazard the redirect
 *     flagged, and it is real** — with per-department numbering, small integers
 *     are common, so `4` matching 14/40/400 is not a corner case.
 *
 * 🔑 **SO THIS TIER IS EXACT over folded tokens.** A name is prose and a partial
 * word is a legitimate query, so those stay SUBSTRING; an identifier is a whole
 * thing quoted complete, so half of one is not a match. The two tiers are OR'd,
 * never chained — a non-identifier query falls through to the unchanged text
 * loop, which is what keeps `AURORA-ORD-120` and every legacy lookup working.
 */

/**
 * 🔀 THE FOLD AND THE TWO-TIER LOOP MOVED TO `utils/textSearchMatch.js` ON
 * 2026-09-13, and the reasoning above moved with a copy of itself — read that
 * file for the rule, this one for the order-specific FIELD LIST and TOKENS.
 * `utils/hireSearch.js` needed the identical predicate over a different row
 * shape; `check-duplication` scored the two at 0.95 and blocked the commit, and
 * the Code Unification Principle says extract, never declare. **No behaviour
 * changed** — `orderSearch.test.js` is unedited across the move and is the
 * proof.
 */

/**
 * Every folded string that names THIS order. Exact membership, never substring.
 *
 * 🪤 The `order_number` guard matters: `formatOrderNumber(null)` is `null`, so an
 * order with no number contributes no `ord...` token — a search for `ord` cannot
 * match a numberless row through this tier.
 */
function orderIdentifierTokens(order) {
  const tokens = new Set();

  const formatted = formatOrderNumber(order.order_number);
  if (formatted) {
    tokens.add(foldIdentifier(formatted));      // ORD-004 → ord4
    tokens.add(foldIdentifier(order.order_number)); // 4 → 4
  }

  // The stored PO is canonicalised (a legacy row holds a bare `147`) then folded,
  // so the prefixed spelling a person types reaches an unprefixed stored value.
  const po = normalisePoNumber(order.po_number);
  if (po) tokens.add(foldIdentifier(po));

  tokens.delete('');
  return tokens;
}

const ORDER_SEARCH_SPEC = {
  fields: SEARCHABLE_FIELDS,
  identifierTokens: orderIdentifierTokens,
};

export function orderMatchesSearch(order, searchTerm) {
  return matchesTextSearch(order, searchTerm, ORDER_SEARCH_SPEC);
}

/**
 * `items_search_text` rebuilt from a row's `preview_items` — the SAME string
 * the server's builder makes (`routes/orders.js`, `GET /api/orders`):
 * each line's `description` + `part_number`, blanks dropped, joined `' | '`.
 *
 * 🔑 Why it exists (2026-09-26 payload trim, owner: *"trim the payload now —
 * no user-visible change"*): `GET /api/orders/department` stopped sending the
 * string because `preview_items` already carries every line's description and
 * part number, UNTRUNCATED — the string was ~5.5% of a 14 MB poll response
 * repeating bytes the row already had. Department Orders rebuilds it at its
 * one fetch site with `withItemsSearchText`, so `SEARCHABLE_FIELDS` and
 * `orderMatchesSearch` are unchanged.
 *
 * 🔴 Call it on DEPARTMENT ORDERS only. To Approve's rows also carry
 * `preview_items` but have never been searchable by item text; stamping them
 * would be a behaviour change nobody ruled on (an owner option, not done).
 * My Orders still receives the server's own copy.
 */
export function itemsSearchText(previewItems) {
  if (!Array.isArray(previewItems)) return '';
  return previewItems
    .map((item) => [item && item.description, item && item.part_number].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(' | ');
}

/** Stamp `items_search_text` onto every list row, in place; returns the same array. */
export function withItemsSearchText(orders) {
  if (!Array.isArray(orders)) return orders;
  for (const order of orders) {
    if (order) order.items_search_text = itemsSearchText(order.preview_items);
  }
  return orders;
}
